// Ayuda EXCLUSIVA de Reclamos de aseguradora. Centro Medico → Resumen → Ayuda Reclamos
// (y enlazada desde la Ayuda del módulo). Patrón ayuda-de-modulo: requisitos +
// flujo del reclamo + ¿cómo hago...? + glosario. Motor data-driven multi-aseguradora
// (Aetna = 1ª plantilla): auto-llena el PDF oficial, enlaza el costo de la factura,
// genera un superbill itemizado y trae la misión desde el centro de costo del paciente.
import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Link2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const REQUISITOS: { label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { label: 'Formato de aseguradora disponible', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'Cada aseguradora es una plantilla data-driven (Aetna GR-68069-21 ya viene cargada). Agregar otra = configurar su PDF base y el mapa de campos (lo hace Sistemas).', advisory: true },
  { label: 'Paciente con N° de asegurado', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'Se guarda en el perfil de la aseguradora del paciente (botón "Guardar perfil al paciente" en la pantalla de reclamo). Debe ser el ID de miembro (10 dígitos).' },
  { label: 'Misión en el centro de costo del paciente', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'El centro de costo del paciente es un partner tipo "misión": grupo = su nombre, número = su identificación, y teléfono/email desde la ficha de ese partner. Cárgalos ahí y salen solos en el formulario.' },
  { label: 'Servicios facturados al paciente', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'El costo se toma de la factura del día (factura emitida al paciente o venta POS). Sin factura, el costo se ingresa a mano.', advisory: true },
  { label: 'Diagnóstico CIE-10 en la consulta', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'La aseguradora liquida contra el diagnóstico; sin CIE-10 el reclamo queda incompleto (lo marca el gate). Se registra en Enfermedad actual de la ficha.' },
];

const FLUJO = [
  { n: 1, titulo: 'Abrir el reclamo', detalle: 'Ficha del paciente → "Reclamos aseguradora" → Nuevo reclamo.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 2, titulo: 'Elegir formato y médico', detalle: 'Selecciona la aseguradora (Aetna) y, opcionalmente, el médico responsable.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 3, titulo: 'Seleccionar las atenciones', detalle: 'Marca las consultas a reclamar (hasta el máximo del formato). Cada una lleva un punto verde/ámbar según si tiene lo necesario (motivo, CIE-10, costo).', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 4, titulo: 'Resolver datos', detalle: 'El sistema arma el formulario: datos del paciente, misión (del centro de costo) y, por atención, fecha, costo (de la factura) y diagnóstico.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 5, titulo: 'Revisar el gate "reclamo listo"', detalle: 'El checklist muestra qué falta (obligatorios en rojo). Completa lo editable ahí mismo; lo clínico (CIE-10, motivo) se corrige en la consulta. El botón Generar se habilita solo cuando está listo.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 6, titulo: 'Guardar el perfil de la aseguradora', detalle: 'Guarda al paciente su N° de asegurado y datos de misión para reutilizarlos en el próximo reclamo.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 7, titulo: 'Generar los PDF', detalle: 'Se produce el formulario OFICIAL de la aseguradora (con los datos estampados) y un SUPERBILL con el detalle itemizado de los servicios.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
  { n: 8, titulo: 'Descargar, firmar y presentar', detalle: 'Descarga ambos PDF desde la ficha. La firma del paciente/autorizado se hace a mano sobre el impreso; se presentan juntos a la aseguradora.', ruta: '/medical/pacientes', tituloTab: 'Pacientes' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo cargo los datos de la misión para que salgan en el formulario?',
    pasos: [
      'El "centro de costo" del paciente es la misión, y es un partner tipo centro de costo.',
      'En la ficha de ESE partner: el nombre es el grupo de la misión, la identificación es el número, y el teléfono/email son los de la misión.',
      'Carga teléfono y email en la ficha del partner-misión (una sola vez por misión).',
      'A partir de ahí, todo reclamo de un paciente de esa misión trae grupo, número, teléfono y email automáticamente.',
    ],
    enlaces: [{ to: '/medical/pacientes', t: 'Pacientes' }],
  },
  {
    q: '¿Por qué el costo sale vacío o en rojo?',
    pasos: [
      'El costo se toma de la factura del paciente del día de la atención (factura emitida al paciente o venta POS registrada).',
      'Si ese día no hay factura al paciente, el costo queda vacío y lo ingresas a mano.',
      'Ojo: si el servicio se facturó a la Iglesia (la factura sale a su nombre), con la configuración actual el costo se busca por el encabezado de la factura, así que no lo tomará automáticamente. En ese caso, ingrésalo a mano o factura al paciente.',
      'Cuando el costo viene de una factura, se muestra "✓ Desde factura" con el número; si lo editas, pasa a manual.',
    ],
    enlaces: [{ to: '/medical/pacientes', t: 'Pacientes' }],
  },
  {
    q: '¿Qué es el superbill y por qué se genera?',
    pasos: [
      'Es la factura itemizada de respaldo: el detalle de cada servicio con su costo, que casi todas las aseguradoras exigen ADEMÁS del formulario oficial.',
      'Se arma con las líneas de la factura del paciente (más el diagnóstico CIE-10) y sale como un segundo PDF junto al reclamo.',
      'Si una atención no tiene líneas facturadas, el superbill pone una línea con el costo del tratamiento.',
      'Descárgalo desde la ficha y preséntalo junto al formulario.',
    ],
    enlaces: [{ to: '/medical/pacientes', t: 'Pacientes' }],
  },
  {
    q: '¿Qué corrijo cuando el gate marca campos en rojo?',
    pasos: [
      'Datos del asegurado (nombres, apellidos, N° de asegurado): se editan ahí mismo en la pantalla del reclamo.',
      'Costo, moneda, fechas de admisión/alta (si fue internado): también se editan en el reclamo.',
      'Diagnóstico CIE-10 y motivo de la visita: son clínicos → se corrigen en la consulta (Enfermedad actual de la ficha), no en el reclamo.',
      'Cuando todos los obligatorios están en verde, el botón "Generar reclamo" se habilita.',
    ],
    enlaces: [{ to: '/medical/pacientes', t: 'Pacientes' }],
  },
  {
    q: '¿Cómo consulto el histórico de reclamos generados?',
    pasos: [
      'Por paciente: en la ficha → "Reclamos aseguradora", el panel Historial lista sus reclamos con descarga del formulario y el superbill.',
      'Global (todos los pacientes): Centro Médico → Clínica → "Consulta de reclamos".',
      'Ahí filtras por fecha, aseguradora, estado y texto (paciente / misión / N° de asegurado), ves el costo total y descargas los PDF; también exportas a CSV.',
    ],
    enlaces: [{ to: '/medical/consulta-reclamos', t: 'Consulta de reclamos' }],
  },
  {
    q: '¿Cómo agrego otra aseguradora además de Aetna?',
    pasos: [
      'El motor es data-driven: cada aseguradora es una plantilla con su PDF oficial y un mapa de dónde va cada dato en el PDF.',
      'Se sube el PDF oficial de esa aseguradora y se definen las coordenadas de sus campos (nombre, N° de asegurado, costo, etc.).',
      'Es una configuración técnica (la hace Sistemas); una vez cargada, aparece como una opción más al crear el reclamo.',
    ],
    enlaces: [],
  },
  {
    q: '¿Dónde firma el paciente y cómo se marca "reembolso a la Iglesia"?',
    pasos: [
      'La firma del paciente/autorizado se hace a MANO sobre el formulario impreso (Sección de autorización).',
      '"Reembolso a la Iglesia" (Sección 2) se marca a mano en el impreso según corresponda; el sistema no lo marca automáticamente.',
    ],
    enlaces: [],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Formato de aseguradora', d: 'Plantilla data-driven con el PDF oficial de la aseguradora y el mapa de sus campos. Aetna (GR-68069-21) es la primera.' },
  { t: 'Overlay', d: 'Estampado de los datos sobre el PDF oficial: el reclamo no es una réplica, es el formulario real relleno.' },
  { t: 'Superbill', d: 'Factura itemizada de respaldo con el detalle de servicios; se exige además del formulario. Se genera como 2º PDF.' },
  { t: 'Centro de costo (misión)', d: 'El partner tipo "misión" al que pertenece el paciente; de su ficha salen grupo, número, teléfono y email de la misión.' },
  { t: 'N° de asegurado', d: 'ID de miembro del paciente en la aseguradora (Aetna: 10 dígitos). Se guarda en el perfil de aseguradora del paciente.' },
  { t: 'Provider PIN', d: 'Código del proveedor (la clínica) exigido por la aseguradora; va como constante en el formato.' },
  { t: 'Gate "reclamo listo"', d: 'Checklist que valida los datos obligatorios; el botón Generar se habilita solo cuando está completo.' },
  { t: 'Origen del costo', d: 'De dónde salió el costo de la atención: factura emitida, venta POS, ambas, o manual.' },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaReclamosAseguradora() {
  const ir = useIr();
  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <FileText className="w-5 h-5" /> Ayuda — Reclamos de aseguradora
      </h1>
      <p className="text-sm text-muted-foreground -mt-2">
        Auto-llenado del formulario oficial de la aseguradora desde la historia clínica: enlaza el costo de la factura,
        genera un superbill itemizado y trae la misión desde el centro de costo del paciente. La guía del resto del
        módulo está en <Ir to="/medical/ayuda" t="Ayuda — Centro Médico" />
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Requisitos para que el reclamo salga completo</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {REQUISITOS.map((c) => (
            <div key={c.label} className="flex items-start gap-2 text-sm">
              <div className="w-1.5 h-1.5 mt-2 rounded-full bg-primary shrink-0" />
              <div>
                <span className="font-medium">{c.label}</span>
                {c.advisory && <span className="ml-1 rounded bg-amber-100 px-1.5 text-[10px] uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">según el caso</span>}
                <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                <Ir to={c.ruta} t={c.titulo} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo del reclamo</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal pl-5 space-y-1">{c.pasos.map((p, i) => <li key={i}>{p}</li>)}</ol>
              {c.enlaces.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-3">{c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}</div>
              )}
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm"><span className="font-medium">{g.t}:</span> <span className="text-muted-foreground">{g.d}</span></div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
